<template>
  <modal
    name="errorReport"
    :adaptive="true"
    width="760"
    height="auto"
    :max-height="9999"
    @closed="onClosed"
  >
    <div
      v-if="report"
      class="font-inter flex flex-col"
      style="max-height: 90vh"
    >
      <div class="flex items-start justify-between px-6 pt-6 pb-3 shrink-0">
        <div class="flex items-center gap-2">
          <h2 class="text-lg font-bold">{{ $t("error-modal-title") }}</h2>
          <span
            v-if="report.duplicateCount > 0"
            class="text-xs bg-warning text-white rounded-full px-2 py-0.5"
          >
            {{ $t("error-modal-duplicates", { count: report.duplicateCount }) }}
          </span>
        </div>
        <button
          type="button"
          class="text-neutral-500 hover:text-neutral-800 text-xl leading-none"
          @click="close"
          aria-label="close"
        >
          &times;
        </button>
      </div>

      <div class="flex-1 overflow-y-auto px-6">
      <div class="border-b border-neutral-200 pb-4 mb-4">
        <div v-if="report.message">
          <p
            dir="auto"
            class="text-2xl font-medium text-neutral-900 leading-relaxed whitespace-pre-wrap break-words"
          >
            {{ report.message }}
          </p>

          <div v-if="translation" class="mt-3">
            <p
              dir="auto"
              class="text-base italic text-neutral-600 leading-relaxed whitespace-pre-wrap break-words"
            >
              {{ translation }}
            </p>
            <p class="mt-1 text-xs text-neutral-400">
              {{ $t("error-modal-translated-via", { source: translationSource }) }}
            </p>
          </div>

          <button
            v-if="!translation && !translationError"
            type="button"
            :disabled="isTranslating"
            class="mt-2 inline-flex items-center gap-2 text-xs text-primary underline disabled:opacity-60 disabled:cursor-not-allowed"
            @click="translate"
          >
            <zid-loader v-if="isTranslating" size="small" />
            <span>{{ isTranslating ? $t("error-modal-translating") : $t("error-modal-translate") }}</span>
          </button>

          <a
            v-if="translationError"
            :href="translateUrl(report.message)"
            target="_blank"
            rel="noopener noreferrer"
            class="mt-2 inline-block text-xs text-primary underline"
          >
            {{ $t("error-modal-translate") }}
          </a>
        </div>

        <div class="mt-4 grid grid-cols-2 gap-3 text-xs text-neutral-600">
          <div>
            <div class="uppercase text-neutral-500">{{ $t("error-modal-page") }}</div>
            <div class="truncate">{{ report.pageName || "—" }}</div>
            <div class="truncate text-neutral-400">{{ report.pageUrl }}</div>
          </div>
          <div>
            <div class="uppercase text-neutral-500">{{ $t("error-modal-when") }}</div>
            <div>{{ report.timestamp }}</div>
          </div>
        </div>

        <p class="mt-3 text-xs text-neutral-500">{{ $t("error-modal-hint") }}</p>
      </div>

      <details class="mb-4">
        <summary class="cursor-pointer text-sm font-semibold text-neutral-700">
          {{ $t("error-modal-technical-details") }}
        </summary>
        <div class="mt-3 space-y-3 text-xs">
          <div>
            <div class="text-neutral-500 uppercase">{{ $t("error-modal-method-url") }}</div>
            <div class="font-mono break-all">{{ report.method }} {{ report.url }}</div>
          </div>
          <div>
            <div class="text-neutral-500 uppercase">{{ $t("error-modal-request-id") }}</div>
            <div class="font-mono break-all">{{ report.requestId || "—" }}</div>
          </div>
          <div>
            <div class="text-neutral-500 uppercase">{{ $t("error-modal-officer") }}</div>
            <div>{{ officerDisplay }}</div>
          </div>
          <div class="grid grid-cols-2 gap-3">
            <div>
              <div class="text-neutral-500 uppercase">{{ $t("error-modal-app-mode") }}</div>
              <div>{{ report.appMode }}</div>
            </div>
            <div>
              <div class="text-neutral-500 uppercase">{{ $t("error-modal-user-agent") }}</div>
              <div class="break-words">{{ report.userAgent }}</div>
            </div>
          </div>
          <div>
            <div class="text-neutral-500 uppercase">{{ $t("error-modal-request-headers") }}</div>
            <pre class="bg-neutral-50 border border-neutral-200 rounded p-2 overflow-auto max-h-40 font-mono">{{ formatJson(report.requestHeaders) }}</pre>
          </div>
          <div>
            <div class="text-neutral-500 uppercase">{{ $t("error-modal-request-body") }}</div>
            <pre class="bg-neutral-50 border border-neutral-200 rounded p-2 overflow-auto max-h-40 font-mono">{{ formatJson(report.requestBody) }}</pre>
          </div>
          <div v-if="hasResponseBody">
            <div class="text-neutral-500 uppercase">{{ $t("error-modal-response-body") }}</div>
            <pre class="bg-neutral-50 border border-neutral-200 rounded p-2 overflow-auto max-h-40 font-mono">{{ formatJson(report.responseBody) }}</pre>
          </div>
          <div>
            <div class="text-neutral-500 uppercase">{{ $t("error-modal-curl") }}</div>
            <pre class="bg-neutral-50 border border-neutral-200 rounded p-2 overflow-auto max-h-40 font-mono whitespace-pre-wrap">{{ report.curl }}</pre>
          </div>
        </div>
      </details>
      </div>

      <div class="shrink-0 flex justify-end gap-2 border-t border-neutral-200 px-6 py-4 bg-white">
        <button
          type="button"
          class="px-4 py-2 rounded border border-neutral-300 text-sm text-neutral-700 hover:bg-neutral-50"
          @click="close"
        >
          {{ $t("error-modal-close") }}
        </button>
        <button
          type="button"
          class="px-4 py-2 rounded border border-primary text-sm text-primary hover:bg-primary hover:text-white"
          @click="copyAll"
        >
          {{ $t("error-modal-copy-all") }}
        </button>
        <button
          type="button"
          :disabled="sendDisabled"
          class="px-4 py-2 rounded bg-primary text-white text-sm disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
          @click="send"
        >
          <zid-loader v-if="isSending" size="small" />
          <span>{{ sendLabel }}</span>
        </button>
      </div>
    </div>
  </modal>
</template>

<script lang="ts">
import Vue from "vue";
import { ZidLoader } from "@zidsa/ui";
import errorReportingService, {
  ErrorReport,
} from "@/services/errorReporting.service";
import translationService from "@/services/translation.service";
import toastService from "@/services/toast.service";

export default Vue.extend({
  name: "ErrorReportModal",
  components: { ZidLoader },
  data() {
    return {
      translation: null as string | null,
      translationSource: null as string | null,
      isTranslating: false,
      translationError: false,
    };
  },
  computed: {
    report(): ErrorReport | null {
      return errorReportingService.state.report;
    },
    visible(): boolean {
      return errorReportingService.state.visible;
    },
    isSending(): boolean {
      return errorReportingService.state.sending;
    },
    sendResult(): "success" | "error" | null {
      return errorReportingService.state.sendResult;
    },
    sendDisabled(): boolean {
      return this.isSending || this.sendResult === "success";
    },
    sendLabel(): string {
      if (this.sendResult === "success") return this.$t("error-modal-sent") as string;
      if (this.isSending) return this.$t("error-modal-sending") as string;
      return this.$t("error-modal-send") as string;
    },
    hasResponseBody(): boolean {
      const body = this.report?.responseBody;
      return body !== null && body !== undefined && body !== "";
    },
    officerDisplay(): string {
      const user = this.$store.state.user;
      if (!user) return "(unauthenticated)";
      return `${user.name || ""} <${user.email || ""}> (#${user.id || ""})`;
    },
  },
  watch: {
    visible(val: boolean) {
      if (val) {
        this.$modal.show("errorReport");
      } else {
        this.$modal.hide("errorReport");
      }
    },
    report() {
      this.translation = null;
      this.translationSource = null;
      this.translationError = false;
      this.isTranslating = false;
    },
  },
  methods: {
    translateUrl(text: string): string {
      return `https://translate.google.com/?sl=ar&tl=en&text=${encodeURIComponent(text)}`;
    },
    formatJson(value: unknown): string {
      if (value === null || value === undefined) return "";
      if (typeof value === "string") {
        if (value.length > 5000) return value.slice(0, 5000) + "\n…(truncated)";
        return value;
      }
      try {
        const str = JSON.stringify(value, null, 2);
        if (str.length > 5000) return str.slice(0, 5000) + "\n…(truncated)";
        return str;
      } catch {
        return String(value);
      }
    },
    async copyAll() {
      if (!this.report) return;
      await navigator.clipboard.writeText(
        errorReportingService.formatAsText(this.report)
      );
      toastService.ShowSuccess("error-modal-copied");
    },
    async send() {
      await errorReportingService.sendToSupport();
    },
    async translate() {
      if (!this.report?.message) return;
      this.isTranslating = true;
      this.translationError = false;
      try {
        const result = await translationService.translate(this.report.message);
        this.translation = result.text;
        this.translationSource = result.source;
      } catch {
        this.translationError = true;
      } finally {
        this.isTranslating = false;
      }
    },
    close() {
      this.$modal.hide("errorReport");
    },
    onClosed() {
      errorReportingService.dismiss();
    },
  },
});
</script>
